import {create, StateMachine} from 'javascript-state-machine';

import type {HeartbeatService} from '../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason, VideoStartFailedReason} from '../enums/VideoStartFailedReason';
import type {AnalyticsStateMachineOptions} from '../types/AnalyticsStateMachineOptions';
import type {AnalyticsEventBase, CustomDataValuesEvent, ErrorEvent, QualityChangeEvent} from '../types/EventData';
import type {StateMachineCallbacks} from '../types/StateMachineCallbacks';
import {isNumber} from '../utils/numberUtils';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {customStateMachineErrorCallback, getAllStatesBut, logStateMachineTransition} from './stateMachineUtils';

export enum State {
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  READY = 'READY',
  /**
   * We track and consider the rebuffering state only when it comes from the {@link State.PLAYING},
   * aka. when the video was playing and stopped because it started to rebuffer.
   */
  REBUFFERING = 'REBUFFERING',
  /**
   * We track and consider the "seeking" state only when it comes from the {@link State.PLAYING},
   * aka. when the video has already started to play and is already playing.
   */
  SEEKING = 'SEEKING',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  UNLOADED = 'UNLOADED',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class THEOplayerAnalyticsStateMachine extends AnalyticsStateMachine {
  constructor(
    stateMachineCallbacks: StateMachineCallbacks,
    playingHeartbeatService: HeartbeatService,
    opts: AnalyticsStateMachineOptions,
  ) {
    super(stateMachineCallbacks, playingHeartbeatService, opts);
  }

  override createStateMachine(opts: AnalyticsStateMachineOptions): StateMachine {
    return create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {name: Event.READY, from: State.SETUP, to: State.READY},

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        // Fallback transition: to handle a missing "play" event and a missing transition from READY to STARTUP
        //
        // Case: MISSING "PLAY" EVENT FROM A PLAY ATTEMPT DURING SLOW SOURCE CHANGE.
        // Problem:
        //    When the source change was triggered, the player started to load the video (probably on slow network),
        //    and we were in the SOURCE_CHANGING state. But when `player.play()` was called during that state, the player
        //    did not fire "play" (player bug?), so this play attempt was not recognized by us, and we have not
        //    transitioned to the STARTUP state. Once the source was loaded, we transitioned to the READY state, but
        //    the player remembered the play attempt from before, so the video actually started to play. But because
        //    of the missing transition to STARTUP, we got stuck in the READY state.
        // Solution:
        //    If we are still in the READY state and there was a "playing" event, it means that the play attempt was not
        //    recognized by us, but the video actually started to play, so we will immediately transition to the PLAYING
        //    state. But before that, we will trigger the "startup" sample with a fixed duration of 1ms, when leaving
        //    the READY state.
        // Found on:
        //    theoplayer@10.10.0
        {name: Event.PLAYING, from: State.READY, to: State.PLAYING},

        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.MANUAL_SOURCE_CHANGE, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        {name: Event.READY, from: State.VIDEOSTART_FAILED, to: State.READY},

        // cover the case when a user double-click the play button.
        // aka. when after the first play click, a video starts to load, and we are in the startup state,
        // but during the loading a user clicks the play button again to pause the video
        {name: Event.PAUSE, from: State.STARTUP, to: State.READY},

        // We can't use a player 'timeupdate' event, because it is fired asap after the play button is hit
        // with currentTime: 0, just to set playback state to beginning / to initial state, and THEOplayer is
        // still loading the asset (a specific THEOplayer behavior)
        {name: Event.PLAYING, from: State.STARTUP, to: State.PLAYING},

        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.REBUFFERING},
        {name: Event.PLAYING, from: State.REBUFFERING, to: State.PLAYING},
        {name: Event.PAUSE, from: State.REBUFFERING, to: State.PAUSE},

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},
        // PAUSE state should be also interrupted with the "play" player event
        // to already start PLAYING state tracking with "attempt to play"
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},

        {name: Event.SEEK, from: State.PLAYING, to: State.SEEKING},
        {name: Event.SEEKED, from: State.SEEKING, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.AUDIO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.FINISH_QUALITY_CHANGE, from: State.QUALITY_CHANGE, to: State.PLAYING},

        {
          name: Event.MANUAL_SOURCE_CHANGE,
          from: getAllStatesBut([State.STARTUP], State),
          to: State.SOURCE_CHANGING,
        },
        {name: Event.READY, from: State.SOURCE_CHANGING, to: State.READY},
        // play attempt during sourceChanging
        {name: Event.PLAY, from: State.SOURCE_CHANGING, to: State.STARTUP},

        // Because of this state transition, we are able to make "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},

        {name: Event.ERROR, from: getAllStatesBut([State.STARTUP, State.VIDEOSTART_FAILED], State), to: State.ERROR},

        // Special case to handle unload from any state
        // Transition is canceled in 'onbefore' callback and handled there.
        {name: Event.UNLOAD, from: '*', to: State.UNLOADED},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onbefore${Event.UNLOAD}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;

          if (from === State.STARTUP) {
            this.stateMachineCallbacks.videoStartFailed(duration, VideoStartFailedReason.PAGE_CLOSED);
          } else {
            this.stateMachineCallbacks.unload(duration, String(from).toLowerCase());
          }

          // Return false to cancel the event (and state transition) to avoid other 'onleaveXXX' callbacks calls.
          // We have to handle these special cases
          // - when the page is unloaded, the browser can't make a standard http requests (we need sendBeacon)
          // - when the player is destroyed, we can't access it to fill all sample properties
          return false;
        },

        [`onleave${State.CUSTOMDATACHANGE}`]: (
          _event,
          _from,
          _to,
          _eventTimestamp: number,
          eventData: CustomDataValuesEvent,
        ) => {
          this.stateMachineCallbacks.customdatachange(undefined, undefined, eventData);
        },

        [`onleave${State.PAUSE}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.pause(duration, 'pause');
        },

        [`onleave${State.PLAYING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.playingHeartbeatService.stopHeartbeat();

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.playing(duration, 'playing');
        },

        [`onleave${State.REBUFFERING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.resetRebufferingHelpers();

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.rebuffering(duration, 'rebuffering');
        },

        [`onleave${State.READY}`]: (event, from, to, _eventTimestamp: number, eventData: AnalyticsEventBase) => {
          // To support the fallback transition for the case: MISSING "PLAY" EVENT FROM A PLAY ATTEMPT DURING SLOW SOURCE CHANGE.
          if (event === Event.PLAYING && to === State.PLAYING) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
            this.stateMachineCallbacks.startup(1, 'startup');
            return;
          }
        },

        [`onleave${State.SEEKING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.seeking(duration);
        },

        [`onleave${State.SETUP}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          // Setting player setup duration to fixed value of 1ms.
          // We can't get a correct setup duration from player, because it does not have an "onInitialized" event,
          // and we can't relay on the video 'loadedmetadata' event, because it is not fired in all cases, like when
          // player is initialized without source preloading (THEOplayer default behavior).
          this.stateMachineCallbacks.setup(1, 'setup');
        },

        [`onleave${State.STARTUP}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.clearVideoStartTimeout();

          // if we are transitioning from STARTUP to READY state, it means that playback was not started (such as when
          // a user clicked pause during video loading), so we do not send a startup sample in that case
          if (to === State.READY) {
            return;
          }

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          if (to === State.VIDEOSTART_FAILED) {
            this.stateMachineCallbacks.videoStartFailed(duration, createVideoStartFailedReason(event));
            return;
          }

          this.stateMachineCallbacks.startup(duration, 'startup');
        },

        onleavestate: (event, from, to) => {
          logStateMachineTransition('LEAVE', event, from, to);
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.REBUFFERING}`]: () => {
          this.startRebufferingHeartbeatInterval();
        },

        [`onenter${State.STARTUP}`]: () => {
          this.setVideoStartTimeout();
        },

        [`onenter${State.QUALITY_CHANGE}`]: (_event, _from, _to, eventTimestamp, eventData: QualityChangeEvent) => {
          // Quality change should produce a "signal sample" and immediately transition us back to PLAYING state
          this.stateMachineCallbacks.qualitychange(eventData);
          this.stateMachine[Event.FINISH_QUALITY_CHANGE](eventTimestamp);
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();
          // if customer set custom "start time" timestamp in AnalyticsStateMachineOptions of adapter constructor,
          // use it and override `onEnterStateTimestamp` during JSM initialization (transition to initial SETUP state)
          if (isNumber(opts.starttime) && event === JSM_INITIAL_EVENT) {
            this.onEnterStateTimestamp = opts.starttime;
          }

          this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
        },

        [`onafter${Event.MANUAL_SOURCE_CHANGE}`]: (event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.manualSourceChange(eventData);
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData: ErrorEvent) => {
          this.stateMachineCallbacks.error(eventData);
        },
      },
    });
  }
}
